import type { ToolRunner } from './base.js';
import type { CheckResult } from '../types/index.js';
import { checkCommand, runCommand, parseSeverity } from './base.js';

interface OsvResult {
  results: Array<{
    source: {
      path: string;
      type: string;
    };
    packages: Array<{
      package: {
        name: string;
        version: string;
        ecosystem: string;
      };
      vulnerabilities: Array<{
        id: string;
        summary: string;
        details: string;
        severity: Array<{
          type: string;
          score: string;
        }>;
        affected: Array<{
          ranges: Array<{
            type: string;
            events: Array<{ introduced?: string; fixed?: string }>;
          }>;
        }>;
        references: Array<{ type: string; url: string }>;
      }>;
    }>;
  }>;
}

export const osvScannerRunner: ToolRunner = {
  name: 'osv-scanner',

  async isInstalled(): Promise<boolean> {
    return checkCommand('osv-scanner');
  },

  async getVersion(): Promise<string | null> {
    const result = await runCommand('osv-scanner', ['--version'], process.cwd());
    if (result.exitCode === 0) {
      return result.stdout.trim();
    }
    return null;
  },

  async run(cwd: string, _files?: string[]): Promise<CheckResult[]> {
    const args = ['--format', 'json', '--recursive', cwd];

    const result = await runCommand('osv-scanner', args, cwd);

    if (result.exitCode === 0 && !result.stdout.trim()) {
      return [];
    }

    try {
      const output: OsvResult = JSON.parse(result.stdout);
      const results: CheckResult[] = [];

      for (const source of output.results || []) {
        for (const pkg of source.packages || []) {
          for (const vuln of pkg.vulnerabilities || []) {
            const severity = vuln.severity?.[0]?.score;
            let severityLevel: 'error' | 'warn' | 'info' = 'warn';

            if (severity) {
              const score = parseFloat(severity);
              if (score >= 7.0) severityLevel = 'error';
              else if (score >= 4.0) severityLevel = 'warn';
              else severityLevel = 'info';
            }

            results.push({
              ruleId: `deps.vulnerability.${vuln.id}`,
              severity: severityLevel,
              message: `${pkg.package.name}@${pkg.package.version}: ${vuln.summary || vuln.id}`,
              file: source.source.path,
            });
          }
        }
      }

      return results;
    } catch {
      if (result.exitCode === 0) {
        return [];
      }
      return [
        {
          ruleId: 'deps.no-vulnerable',
          severity: 'error',
          message: `OSV Scanner error: ${result.stderr || 'Unknown error'}`,
        },
      ];
    }
  },
};
